import { Button, ButtonGroup, Dialog, InputText, Loading, Radio, SelectCustom } from '@/components';
import { ReactComponent as Trash } from '@/assets/images/svg/lixeira.svg';
import { States } from '@/helpers/common/states';
import { ActionProps } from '@/components/Dialog';
import React, { Fragment } from 'react';
import { FormData, FormErrors, OnChangeFormInput, UseFormReturn } from '@/hooks';
import { Col, Container, FormGroup, Row } from 'reactstrap';
import { ArrowLeft, Plus, X } from 'react-feather';
import { colors } from '@/styles/colors';
import { Link } from 'react-router-dom';
import { CustomTable } from '@/components/Table';
import Event from '@/model/Event';
import dayjs from 'dayjs';
import { toCurrency } from '@/helpers/masks/toCurrency';
import EventSiteGet from '@/model/EventSiteGet';
import TicketSite from '@/model/TicketSite';
import CheckDiscount from '@/model/CheckDiscount';
import CreateOrderAdmResponse from '@/model/CreateOrderAdmResponse';
import TicketPdf from '@/model/TicketPdf';
import { updateMask as moneyMask, unmask as moneyCash } from '@/helpers/masks/cashNumber';
import EventMap from '@/model/EventMap';
import ChartObject from '@/model/ChartObject';
import ChartSelectedTicketType from '@/model/ChartSelectedTicketType';
import { SelectedObject } from '@/model/SelectedObject';
import {
  FormInputName,
  OnShouldShowModalProps,
  ShouldShowModal,
  UserDataFormInputName,
} from '../../types';
import { QrCodeContent } from '../../components/QrCodeContent';
import { TicketContent } from '../../components/TicketContent';
import { UrlContent } from '../../components/UrlContent';
import { MapContent } from '../../components/MapContent';
import './style.css';

interface StateProps {
  state: States;
  title: React.ReactNode | string;
  visible: boolean;
  shouldShowModal: ShouldShowModal;
  formData: FormData;
  formErrors: FormErrors;
  events: Event[];
  event?: EventSiteGet;
  eventMap?: EventMap;
  eventKey?: string;
  selectedObjects: SelectedObject[];
  amount: number;
  totalValue: number;
  fee: number;
  discount?: CheckDiscount;
  formControllerUserData: UseFormReturn;
  totalWithFee: number;
  disableName: boolean;
  disableEmail: boolean;
  disablePhone: boolean;
  disableBirthDate: boolean;
  disableMotherName: boolean;
  response?: CreateOrderAdmResponse;
  hasFractionalPayment: boolean;
  minFractionalAmount?: number;
  fractionalAmount: number;
  isFractionalSale: boolean;
}

interface DispatchProps {
  onToggle: () => void;
  onChangeFormInput: OnChangeFormInput;
  onSelectEvent: (eventId: string) => void;
  onObjectSelected: (object: ChartObject, selectedTicketType: ChartSelectedTicketType) => void;
  onObjectDeselected: (object: ChartObject, selectedTicketType: ChartSelectedTicketType) => void;
  onAddTicket: (ticketId: string, isHalfPrice: boolean) => void;
  onRemoveTicket: (ticketId: string, isHalfPrice: boolean) => void;
  onAddDiscount: () => void;
  onRemoveDiscount: () => void;
  onShouldShowModal: (props: OnShouldShowModalProps) => void;
  onCheckCpf: (cpf: string) => void;
  save: () => void;
  onCopyUrl: (url: string) => void;
  onRemoveItemMap: (sectionId: string, ticketId: string, label: string) => void;
  onChangeFractionalAmount: (v: number) => void;
  onToggleFractionalSale: (b: boolean) => void;
}

type Props = StateProps & DispatchProps;

export const CreateSaleContainer: React.FC<Props> = ({
  state,
  title,
  visible,
  shouldShowModal,
  formData,
  formErrors,
  events,
  event,
  eventMap,
  eventKey,
  selectedObjects,
  discount,
  formControllerUserData,
  amount,
  totalValue,
  fee,
  totalWithFee,
  disableName,
  disableEmail,
  disablePhone,
  disableBirthDate,
  disableMotherName,
  response,
  hasFractionalPayment,
  minFractionalAmount,
  fractionalAmount,
  isFractionalSale,
  onToggle,
  onChangeFormInput,
  onSelectEvent,
  onObjectSelected,
  onObjectDeselected,
  onAddTicket,
  onRemoveTicket,
  onAddDiscount,
  onRemoveDiscount,
  onShouldShowModal,
  onCheckCpf,
  save,
  onCopyUrl,
  onRemoveItemMap,
  onChangeFractionalAmount,
  onToggleFractionalSale,
}): JSX.Element => {
  const renderActionDialogToClose: ActionProps = {
    title: 'Fechar',
    onClick: (): void => onToggle(),
    theme: 'noneBorder',
  };
  const getTickets = (sectionId: string): TicketSite[] => {
    const tickets: TicketSite[] = [];
    if (event && event.sections && event.sections.length > 0) {
      // eslint-disable-next-line no-plusplus
      for (let s = 0; s < event.sections.length; s++) {
        if (event.sections[s].section.id === sectionId) {
          if (event.sections[s].tickets && event.sections[s].tickets.length > 0) {
            event.sections[s].tickets.forEach(data => {
              tickets.push(data);
            });
          }
          break;
        }
      }
    }
    return tickets;
  };

  const getEventKey = (ticketId: string): string => {
    let value = '';
    if (eventMap && eventMap.sections && eventMap.sections.length > 0) {
      // eslint-disable-next-line no-plusplus
      for (let s = 0; s < eventMap.sections.length; s++) {
        if (eventMap.sections[s].tickets && eventMap.sections[s].tickets.length > 0) {
          // eslint-disable-next-line no-plusplus
          for (let t = 0; t < eventMap.sections[s].tickets.length; t++) {
            if (eventMap.sections[s].tickets[t].ticket.id === ticketId) {
              value = eventMap.sections[s].tickets[t].eventKey;
              break;
            }
          }
        }
        if (value.length > 0) {
          break;
        }
      }
    }
    return value;
  };

  const mountValue = (unitValue: number, feeValue: number): string => {
    let value = toCurrency(unitValue);
    if (feeValue && feeValue > 0) {
      const calc = Number(unitValue) * Number(feeValue) - Number(unitValue);
      value += ` + Taxa ${toCurrency(calc)}`;
    }
    return value;
  };

  const getDiscountText = (): string => {
    let value = '';
    if (discount) {
      if (discount.discountCoupon) {
        value = discount.discountCoupon.name;
      } else if (discount.voucher) {
        value = discount.voucher.code;
      }
    }
    return value;
  };

  const getDiscountValue = (): number => {
    let value = 0;
    if (discount) {
      if (discount.discountCoupon) {
        value = Number(discount.discountCoupon.discountValue);
      } else if (discount.voucher) {
        value = Number(discount.voucher.value);
      }
    }
    return value;
  };

  const getValidTickets = (): TicketSite[] => {
    const tickets: TicketSite[] = [];
    if (event && event.sections && event.sections.length > 0) {
      event.sections.forEach(eventSection => {
        if (eventSection.tickets && eventSection.tickets.length > 0) {
          eventSection.tickets.forEach(data => {
            if (
              data.amount &&
              data.amount > 0 &&
              data.hasMap &&
              data.lables &&
              data.lables.length > 0
            ) {
              data.lables.forEach(label => {
                const split = label.split('-');
                tickets.push({
                  ...data,
                  name: `${split[0]} ${split[1]}`,
                  amount: 1,
                  labelName: label,
                });
              });
            } else if (data.amount && data.amount > 0 && !data.hasMap) {
              tickets.push(data);
            }
          });
        }
      });
    }
    return tickets;
  };

  return (
    <React.Fragment>
      <Loading isVisible={state === States.loading} />
      <Dialog
        title={title}
        visible={visible}
        onClose={onToggle}
        isContentWithCard={true}
        actions={[
          {
            [ShouldShowModal.map]: renderActionDialogToClose,
            [ShouldShowModal.qrCode]: renderActionDialogToClose,
            [ShouldShowModal.ticket]: renderActionDialogToClose,
            [ShouldShowModal.url]: renderActionDialogToClose,
          }[shouldShowModal],
          {
            [ShouldShowModal.map]: {},
            [ShouldShowModal.qrCode]: {},
            [ShouldShowModal.ticket]: {},
            [ShouldShowModal.url]: {},
          }[shouldShowModal],
        ]}
      >
        {
          {
            [ShouldShowModal.map]: (
              <MapContent
                eventMap={eventMap}
                eventKey={eventKey}
                selectedObjects={selectedObjects}
                onObjectSelected={onObjectSelected}
                onObjectDeselected={onObjectDeselected}
              />
            ),
            [ShouldShowModal.qrCode]: (
              <QrCodeContent
                qrCode={response?.qrCode as string}
                url={response?.url as string}
                onCopyUrl={onCopyUrl}
              />
            ),
            [ShouldShowModal.ticket]: (
              <TicketContent
                tickets={response?.tickets as TicketPdf[]}
                url={response?.url as string}
                onCopyUrl={onCopyUrl}
              />
            ),
            [ShouldShowModal.url]: (
              <UrlContent url={response?.url as string} onCopyUrl={onCopyUrl} />
            ),
          }[shouldShowModal]
        }
      </Dialog>
      <Container className="mainContainer" fluid={true}>
        <div className="create-sale-header">
          <Link to={`${process.env.PUBLIC_URL}/dashboard/event`}>
            <ArrowLeft color={colors.black} className="arrow-left" />
          </Link>
          <div>
            <h5 className="create-sale-title">Criar pedido</h5>
            <p className="create-sale-subtitle">
              Selecione o evento, adicione ingressos e finalize a venda.
            </p>
          </div>
        </div>

        <div className="create-sale-layout">
          <div>
            <div className="create-sale-card">
              <div className="create-sale-card-title">Registrar</div>

              <Row>
                <Col md={12}>
                  <FormGroup className="mb-2">
                    <SelectCustom
                      name="eventId"
                      label="Evento"
                      placeholder="Digite ou selecione o evento"
                      onChange={e => {
                        onChangeFormInput(FormInputName.eventId)(e?.value as string);
                        onSelectEvent(e?.value as string);
                      }}
                      error={formErrors.eventId && formErrors.eventId[0]}
                      value={formData[FormInputName.eventId]}
                      options={events.map(item => ({
                        value: item.id,
                        label: `${item.name} - ${dayjs(item.startDate).format('DD/MM/YYYY')}`,
                      }))}
                    />
                  </FormGroup>
                </Col>
              </Row>

              {event && event.sections && event.sections.length > 0 && (
                <>
                  <Row>
                    <Col md={12}>
                      <FormGroup className="mb-2">
                        <SelectCustom
                          name="sectionId"
                          label="Setor"
                          placeholder="Digite ou selecione o setor"
                          onChange={e => {
                            onChangeFormInput(FormInputName.sectionId)(e?.value as string);
                          }}
                          error={formErrors.sectionId && formErrors.sectionId[0]}
                          value={formData[FormInputName.sectionId]}
                          options={event.sections.map(item => ({
                            value: item.section.id,
                            label: item.section.name,
                          }))}
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  {formData[FormInputName.sectionId] &&
                    formData[FormInputName.sectionId].trim().length > 0 && (
                      <Row>
                        <Col md={12}>
                          <div className="create-sale-divider" />

                          <div className="create-sale-card-title create-sale-card-title--tight">
                            Ingressos disponíveis
                          </div>

                          <CustomTable
                            columns={[
                              { name: 'Ingresso', selector: row => row.name },
                              { name: 'Valor', selector: row => row.unitValue },
                              { name: '', selector: row => row.actions, right: true },
                              { name: 'Total', selector: row => row.total, right: true },
                            ]}
                            data={getTickets(formData[FormInputName.sectionId]).map(data => {
                              const selectedTicketId = getValidTickets()[0]?.id ?? null;
                              const lockOtherTickets =
                                isFractionalSale &&
                                !!selectedTicketId &&
                                selectedTicketId !== data.id;
                              const value = {
                                name: data.name,
                                unitValue: mountValue(data.unitValue, data.fee),
                                actions: data.hasMap ? (
                                  <button
                                    className="select-btn"
                                    onClick={(): void =>
                                      onShouldShowModal({
                                        value: ShouldShowModal.map,
                                        newTitleModal: 'Mapa',
                                        eventKey: getEventKey(data.id),
                                      })
                                    }
                                  >
                                    Selecionar
                                  </button>
                                ) : (
                                  <div className="stepper">
                                    <div
                                      className={`stepper-btn ${
                                        data.amount === undefined ||
                                        data.amount === 0 ||
                                        lockOtherTickets
                                          ? 'is-disabled'
                                          : ''
                                      }`}
                                      onClick={(): void => {
                                        if (lockOtherTickets) return;
                                        onRemoveTicket(data.id, data.isHalfPrice);
                                      }}
                                    >
                                      –
                                    </div>

                                    <div className="stepper-mid">{data.amount || 0}</div>

                                    <div
                                      className={`stepper-btn ${
                                        data.count > (data.amount || 0) && !lockOtherTickets
                                          ? ''
                                          : 'is-disabled'
                                      }`}
                                      onClick={(): void => {
                                        if (lockOtherTickets) return;
                                        onAddTicket(data.id, data.isHalfPrice);
                                      }}
                                    >
                                      +
                                    </div>
                                  </div>
                                ),
                                total: toCurrency(data.totalValue || 0),
                              };
                              return value;
                            })}
                            theme="secondary"
                            progressPending={false}
                            numberRowsPerPage={1000}
                          />
                        </Col>
                      </Row>
                    )}
                </>
              )}
            </div>

            {getValidTickets().length > 0 && (
              <div className="create-sale-card">
                <div className="create-sale-card-title">Carrinho</div>

                <div className="table-scroll">
                  <div className="table-min-width">
                    <CustomTable
                      columns={[
                        { name: 'Ingresso', selector: row => row.name, width: '35%' },
                        { name: 'Qtd', selector: row => row.amount, center: true },
                        {
                          name: 'Valor',
                          selector: row => row.unitValue,
                          minWidth: '250px',
                          center: true,
                        },
                        { name: 'Total', selector: row => row.total, right: true },
                        { name: '', selector: row => row.actions },
                      ]}
                      data={getValidTickets().map(data => {
                        const value = {
                          name: data.name,
                          amount: data.amount,
                          unitValue: mountValue(data.unitValue, data.fee),
                          total: toCurrency(data.totalValue || 0),
                          actions: data.hasMap ? (
                            <Trash
                              className="svg-icon action-icon svg-icon-trash"
                              onClick={() => {
                                onRemoveItemMap(
                                  formData[FormInputName.sectionId],
                                  data.id,
                                  data.labelName as string,
                                );
                              }}
                            />
                          ) : (
                            ''
                          ),
                        };
                        return value;
                      })}
                      theme="secondary"
                      progressPending={false}
                      numberRowsPerPage={1000}
                    />
                  </div>
                </div>
              </div>
            )}

            {totalValue > 0 && (
              <div className="create-sale-card">
                {totalValue > 0 && (
                  <Fragment>
                    <div className="create-sale-card-title">Cupom de desconto</div>

                    <Row>
                      <Col md={11}>
                        <FormGroup className="mb-2">
                          {!discount ? (
                            <InputText
                              name="code"
                              label="Cupom (apenas um por compra)"
                              placeholder="ABCD1234"
                              value={formData[FormInputName.code]}
                              onChange={e => onChangeFormInput(FormInputName.code)(e.target.value)}
                              error={formErrors.code && formErrors.code[0]}
                            />
                          ) : (
                            <InputText
                              name="discount"
                              label="Cupom (apenas um por compra)"
                              value={getDiscountText()}
                              disabled
                              onChange={window.console.log}
                            />
                          )}
                        </FormGroup>
                      </Col>
                      <Col md={1} className="pr-0">
                        <div style={{ paddingTop: 34, display: 'flex', justifyContent: 'center' }}>
                          {!discount ? (
                            <Plus className="svg-icon action-icon" onClick={onAddDiscount} />
                          ) : (
                            <X className="svg-icon action-icon" onClick={onRemoveDiscount} />
                          )}
                        </div>
                      </Col>
                    </Row>
                    {hasFractionalPayment && (
                      <>
                        <Row>
                          <Col md={8}>
                            <FormGroup className="mb-2">
                              <ButtonGroup
                                label="Venda fracionada?"
                                name="fractionalSale"
                                value={formData[FormInputName.fractionalSale]}
                                onChange={e => {
                                  onToggleFractionalSale(e.target.value === 'true');
                                  onChangeFormInput(FormInputName.fractionalSale)(e.target.value);
                                }}
                                options={[
                                  { value: false, label: 'Não' },
                                  { value: true, label: 'Sim' },
                                ]}
                              />
                            </FormGroup>
                          </Col>
                        </Row>

                        {isFractionalSale && (
                          <Row>
                            <Col md={8}>
                              <InputText
                                name="fractionalAmount"
                                label="Valor pago agora"
                                placeholder="Ex: 100,00"
                                addon="R$"
                                value={moneyCash(formData[FormInputName.fractionalAmount])}
                                onChange={e => {
                                  const masked = moneyMask(e.target.value);
                                  onChangeFormInput(FormInputName.fractionalAmount)(masked);
                                  const unmasked = moneyCash(masked);
                                  const value = Number(unmasked || 0);
                                  onChangeFractionalAmount(value);
                                }}
                              />
                              {minFractionalAmount && (
                                <p>{`Mínimo permitido: ${toCurrency(minFractionalAmount)}`}</p>
                              )}
                            </Col>
                          </Row>
                        )}
                      </>
                    )}
                  </Fragment>
                )}

                <div className="create-sale-card-title">Pagamento</div>
                <Row>
                  <Col md={8}>
                    <FormGroup className="mb-2">
                      <span className="filter-name"></span>
                      <Radio
                        options={[
                          { label: 'Site', value: 'site' },
                          { label: 'POS', value: 'pos' },
                          { label: 'Dinheiro', value: 'money' },
                        ]}
                        label="Formas de pagamento"
                        name="paymentForm"
                        style={{ fontSize: '18px' }}
                        value={formData[FormInputName.paymentForm]}
                        onChange={e => onChangeFormInput(FormInputName.paymentForm)(e.target.value)}
                        error={formErrors.paymentForm && formErrors.paymentForm[0]}
                      />
                    </FormGroup>
                  </Col>
                </Row>

                {(formData[FormInputName.paymentForm] === 'money' ||
                  formData[FormInputName.paymentForm] === 'pos') && (
                  <>
                    <div className="create-sale-divider" />

                    <FormGroup className="mb-2">
                      <ButtonGroup
                        label="Informar dados do comprador?"
                        name="showUserData"
                        value={formData[FormInputName.showUserData]}
                        onChange={e =>
                          onChangeFormInput(FormInputName.showUserData)(e.target.value)
                        }
                        options={[
                          { value: false, label: 'Não' },
                          { value: true, label: 'Sim' },
                        ]}
                        error={formErrors.showUserData && formErrors.showUserData[0]}
                        // disabled={!!isFractionalSale}
                      />
                    </FormGroup>

                    {isFractionalSale && (
                      <p style={{ marginTop: 8, color: '#b91c1c', fontSize: 13 }}>
                        Para vendas Fracionadas é nescessario informar os dados do cliente
                      </p>
                    )}

                    {formData[FormInputName.showUserData] === 'true' && (
                      <>
                        <Row>
                          <Col md={12}>
                            <InputText
                              name="document"
                              label="CPF"
                              placeholder="123.456.789-00"
                              value={
                                formControllerUserData.formData[UserDataFormInputName.document]
                              }
                              onChange={e =>
                                formControllerUserData.onChangeFormInput(
                                  UserDataFormInputName.document,
                                )(e.target.value)
                              }
                              onBlur={e => onCheckCpf(e.target.value)}
                              error={
                                formControllerUserData.formErrors.document &&
                                formControllerUserData.formErrors.document[0]
                              }
                            />
                          </Col>
                        </Row>

                        <Row>
                          <Col md={12}>
                            <InputText
                              name="name"
                              label="Nome"
                              placeholder="Maria da Silva"
                              value={formControllerUserData.formData[UserDataFormInputName.name]}
                              disabled={disableName}
                              onChange={e =>
                                formControllerUserData.onChangeFormInput(
                                  UserDataFormInputName.name,
                                )(e.target.value)
                              }
                              error={
                                formControllerUserData.formErrors.name &&
                                formControllerUserData.formErrors.name[0]
                              }
                            />
                          </Col>
                        </Row>

                        <Row>
                          <Col md={12}>
                            <InputText
                              name="email"
                              label="E-mail"
                              placeholder="meuemail@email.com"
                              value={formControllerUserData.formData[UserDataFormInputName.email]}
                              disabled={disableEmail}
                              onChange={e =>
                                formControllerUserData.onChangeFormInput(
                                  UserDataFormInputName.email,
                                )(e.target.value)
                              }
                              error={
                                formControllerUserData.formErrors.email &&
                                formControllerUserData.formErrors.email[0]
                              }
                            />
                          </Col>
                        </Row>

                        <Row>
                          <Col md={12}>
                            <InputText
                              name="phone"
                              label="Telefone"
                              placeholder="(00) 0 0000-0000"
                              value={formControllerUserData.formData[UserDataFormInputName.phone]}
                              disabled={disablePhone}
                              onChange={e =>
                                formControllerUserData.onChangeFormInput(
                                  UserDataFormInputName.phone,
                                )(e.target.value)
                              }
                              error={
                                formControllerUserData.formErrors.phone &&
                                formControllerUserData.formErrors.phone[0]
                              }
                            />
                          </Col>
                        </Row>

                        <Row>
                          <Col md={12}>
                            <InputText
                              name="birthDate"
                              label="Data de Nascimento"
                              placeholder="00/00/0000"
                              value={
                                formControllerUserData.formData[UserDataFormInputName.birthDate]
                              }
                              disabled={disableBirthDate}
                              onChange={e =>
                                formControllerUserData.onChangeFormInput(
                                  UserDataFormInputName.birthDate,
                                )(e.target.value)
                              }
                              error={
                                formControllerUserData.formErrors.birthDate &&
                                formControllerUserData.formErrors.birthDate[0]
                              }
                            />
                          </Col>
                        </Row>

                        <Row>
                          <Col md={12}>
                            <InputText
                              name="motherName"
                              label="Nome da mãe"
                              placeholder="Maria da Silva"
                              value={
                                formControllerUserData.formData[UserDataFormInputName.motherName]
                              }
                              disabled={disableMotherName}
                              onChange={e =>
                                formControllerUserData.onChangeFormInput(
                                  UserDataFormInputName.motherName,
                                )(e.target.value)
                              }
                              error={
                                formControllerUserData.formErrors.motherName &&
                                formControllerUserData.formErrors.motherName[0]
                              }
                            />
                          </Col>
                        </Row>
                      </>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          <div className="create-sale-summary">
            <div className="create-sale-card">
              <div className="create-sale-card-title">Resumo</div>

              <div className="summary-line">
                <span>Total de itens</span>
                <strong>{amount}</strong>
              </div>

              <div className="summary-line">
                <span>Sub total</span>
                <strong>{toCurrency(totalValue)}</strong>
              </div>

              <div className="summary-line">
                <span>Taxa</span>
                <strong>{toCurrency(fee)}</strong>
              </div>

              {discount && (
                <div className="summary-line">
                  <span>Desconto</span>
                  <strong>- {toCurrency(getDiscountValue())}</strong>
                </div>
              )}

              {/* <div style={ui.divider} /> */}

              <div className="summary-total">
                <span>Valor total</span>
                <span>{toCurrency(isFractionalSale ? fractionalAmount : totalWithFee)}</span>
              </div>
              <hr />
              <div className=" card-actions-end" style={{ marginTop: 30 }}>
                <Button
                  title="Registrar Venda"
                  onClick={() => save()}
                  style={{ marginRight: 0 }}
                  disabled={totalValue === undefined || totalValue === 0}
                />
              </div>
            </div>
          </div>
        </div>
      </Container>
    </React.Fragment>
  );
};
